import React, {useEffect} from "react";
import Layout, {Header, Main} from "../components/Layout";
import ReactDocumentTitle from "../components/ReactDocumentTitle";
import {useTranslation} from "react-i18next";
import Container from "../ui/container";
import {useQuery} from "@apollo/client";
import {query} from "./home.graphql";
import PlaythroughsWidget from "../components/home/PlaythroughsWidget";
import {isUserAdmin, isUserEditor, isUserManager, isUserStudent, isUserTrainer} from "../types";

import classes from "./home.module.css";
import {useHistory} from "react-router";
import {reverse} from "../routing";
import {FlaskIcon} from "../ui/icons";
import {isBrowserSupported} from "../browser";
import BrowserNotSupportedScene from "./system/browser-not-supported";
import HomeChallengeCard from "../components/HomeChallengeCard";
import ReactLink from "../components/ReactLink";

export default function HomeScene() {
  const {t} = useTranslation();
  const history = useHistory();

  const browserSupported = React.useMemo(() => isBrowserSupported(), []);

  const {data} = useQuery(query, {fetchPolicy: "cache-first"});

  const loaded = !!data;
  const account = data?.account;
  const user = data?.user;
  const contracts = data?.contracts;

  const loggedIn = account && user;

  useEffect(() => {
    if (loaded && !loggedIn) {
      history.push(reverse("login"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, loaded, loggedIn]);

  useEffect(() => {
    if (loggedIn && isUserStudent(user)) {
      history.push(reverse("player"));
    } else if (loggedIn && isUserEditor(user)) {
      history.push(reverse("editor"));
    }
  }, [history, user, loggedIn]);

  if (!browserSupported) {
    return <BrowserNotSupportedScene />;
  }

  if (!loaded || !loggedIn) {
    return null;
  }

  return (
    <>
      <ReactDocumentTitle title={t("home.title")} />

      <Layout data-testid="homeScene" className={classes.layout}>
        <Header type="transparent" />

        <Main>
          <Container className={classes.root}>
            <div className={classes.hello}>
              <div className={classes.helloTop}>
                <h1 className={classes.h1}>
                  {user?.name ? t("home.helloUser", {name: user?.name}) : t("home.hello")}{" "}
                  {user.isDeveloper && <FlaskIcon />}
                </h1>
                <p className={classes.quote}>{t("home.quote")}</p>
              </div>

              {(isUserAdmin(user) || isUserTrainer(user) || isUserManager(user)) && (
                <PlaythroughsWidget account={account} contracts={contracts} />
              )}

              {!!user && !!user.email && (
                <div className={classes.helloBottom}>
                  <div className={classes.userEmailLabel}>{t("home.userEmail")}</div>
                  <div className={classes.userEmailValue}>{user.email}</div>
                </div>
              )}
            </div>

            <div className={classes.cards}>
              <HomeChallengeCard
                as={ReactLink}
                href={reverse("editor")}
                className={classes.card}
                headline={t("home.editorCard.headline")}
                image={require("../assets/home-challenge/card-editor.svg").default}
                title={t("home.editorCard.title")}
                description={t("home.editorCard.description")}
              />

              <HomeChallengeCard
                className={classes.card}
                as={ReactLink}
                href={reverse("player")}
                headline={t("home.playerCard.headline")}
                image={require("../assets/home-challenge/card-player.svg").default}
                title={t("home.playerCard.title")}
                description={t("home.playerCard.description")}
              />
            </div>
          </Container>
        </Main>
      </Layout>
    </>
  );
}
